import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { FaCheck, FaLock, FaArrowRight, FaCrown, FaUsers } from 'react-icons/fa';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';

function Pricing() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSeatModal, setShowSeatModal] = useState(false);
  const [teamSeats, setTeamSeats] = useState(2);

  const plans = [
    {
      name: 'Free',
      price: '$0',
      priceEur: '€0',
      period: 'forever',
      description: 'Perfect for personal use',
      features: [
        '10 file shares per month',
        '30MB max file size',
        '5 downloads per file',
        '24 hour expiry max',
        '1 intake portal',
        'Password protection',
        'QR code sharing'
      ],
      cta: isAuthenticated && user?.plan === 'free' ? 'Current Plan' : 'Get Started',
      popular: false,
      icon: <FaLock className="text-2xl" />
    },
    {
      name: 'Pro',
      price: '$12',
      priceEur: '€11',
      period: 'per month',
      description: 'For professionals and power users',
      features: [
        'Unlimited file shares',
        '2GB max file size',
        '100 downloads per file',
        '30 day expiry max',
        'Unlimited intake portals',
        'Password protection',
        'Custom branding',
        'Priority support'
      ],
      cta: isAuthenticated && user?.plan === 'pro' ? 'Current Plan' : 'Upgrade to Pro',
      popular: true,
      icon: <FaCrown className="text-2xl" />,
      planId: 'pro'
    },
    {
      name: 'Team',
      price: '$8',
      priceEur: '€7',
      period: 'per user/month',
      description: 'For teams and organizations',
      features: [
        'Everything in Pro',
        '5GB max file size',
        '1000 downloads per file',
        '90 day expiry max',
        'Team management',
        'Audit logs',
        'Advanced analytics',
        'Minimum 2 users'
      ],
      cta: isAuthenticated && user?.plan === 'team' ? 'Current Plan' : 'Upgrade to Team',
      popular: false,
      icon: <FaUsers className="text-2xl" />,
      planId: 'team'
    }
  ];

  const handleUpgrade = async (planId, quantity = 1) => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }

    if (user?.plan === planId) {
      return; // Already on this plan
    }

    // Show seat selector for team plan
    if (planId === 'team' && !showSeatModal) {
      setShowSeatModal(true);
      return;
    }

    setLoading(true);
    setError('');
    setShowSeatModal(false);

    try {
      const response = await axios.post(`${API_URL}/stripe/create-checkout-session`, {
        plan: planId,
        quantity: planId === 'team' ? quantity : 1
      });

      if (response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        setError('Failed to create checkout session');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start checkout process');
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/stripe/portal`);
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      setError('Failed to open customer portal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <SEO
        title="Pricing - Free, Pro & Team Plans"
        description="Simple, transparent pricing for secure file sharing. Start free with 10 shares/month. Upgrade to Pro for unlimited shares and 2GB files, or Team for enterprise features."
        url="https://miragedocs.com/pricing"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "MirageDocs Secure File Sharing",
            "description": "End-to-end encrypted file sharing with self-destructing links",
            "brand": { "@type": "Organization", "name": "MirageDocs" },
            "offers": [
              { "@type": "Offer", "name": "Free Plan", "price": "0", "priceCurrency": "EUR", "availability": "https://schema.org/InStock" },
              { "@type": "Offer", "name": "Pro Plan", "price": "11", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "priceSpecification": { "@type": "UnitPriceSpecification", "billingDuration": "P1M" } },
              { "@type": "Offer", "name": "Team Plan", "price": "7", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "priceSpecification": { "@type": "UnitPriceSpecification", "billingDuration": "P1M", "referenceQuantity": { "@type": "QuantitativeValue", "value": "1", "unitText": "user" } } }
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "Can I change plans later?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately." } },
              { "@type": "Question", "name": "What payment methods do you accept?", "acceptedAnswer": { "@type": "Answer", "text": "We accept all major credit cards through Stripe. Your payment information is secure and never stored on our servers." } },
              { "@type": "Question", "name": "Is my data encrypted?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! All files are encrypted client-side using AES-256 encryption before upload. We never see your files in plaintext." } },
              { "@type": "Question", "name": "Can I cancel anytime?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. Cancel your subscription anytime from your dashboard. You'll continue to have access until the end of your billing period." } }
            ]
          }
        ]}
      />
      {/* Team Seat Selection Modal */}
      {showSeatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Select Team Size</h3>
            <p className="text-gray-600 mb-6">How many team members do you need?</p>

            <div className="flex items-center justify-center gap-4 mb-6">
              <button
                onClick={() => setTeamSeats(Math.max(2, teamSeats - 1))}
                className="w-12 h-12 rounded-full bg-gray-200 hover:bg-gray-300 text-2xl font-bold"
              >
                -
              </button>
              <span className="text-4xl font-bold text-gray-900 w-20 text-center">{teamSeats}</span>
              <button
                onClick={() => setTeamSeats(teamSeats + 1)}
                className="w-12 h-12 rounded-full bg-gray-200 hover:bg-gray-300 text-2xl font-bold"
              >
                +
              </button>
            </div>

            <div className="text-center mb-6">
              <span className="text-3xl font-bold text-gray-900">€{teamSeats * 7}</span>
              <span className="text-gray-600">/month</span>
              <p className="text-sm text-gray-500 mt-1">{teamSeats} users × €7/user</p>
              <p className="text-xs text-gray-400 mt-1">Minimum 2 users required</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowSeatModal(false)}
                className="flex-1 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpgrade('team', teamSeats)}
                disabled={loading}
                className="flex-1 py-3 bg-[#2186EB] text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Secure file sharing with end-to-end encryption. Start free, upgrade when you need more.
          </p>
        </div>

        {error && (
          <div className="max-w-4xl mx-auto mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl shadow-lg p-8 relative ${
                plan.popular ? 'ring-2 ring-[#2186EB] scale-105' : ''
              } ${isAuthenticated && user?.plan === plan.name.toLowerCase() ? 'border-2 border-green-500' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-[#2186EB] text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
              )}

              {isAuthenticated && user?.plan === plan.name.toLowerCase() && (
                <div className="absolute -top-4 right-4">
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    Current
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <div className="flex justify-center mb-4 text-[#2186EB]">
                  {plan.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 mb-4">{plan.description}</p>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-2xl text-gray-500 ml-1">/ {plan.priceEur}</span>
                  <div className="text-gray-600 mt-1">{plan.period}</div>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <FaCheck className="text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {isAuthenticated && user?.plan === plan.name.toLowerCase() ? (
                <div>
                  <button
                    disabled
                    className="w-full py-3 bg-gray-200 text-gray-600 rounded-lg font-semibold cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                  {user?.plan !== 'free' && (
                    <button
                      onClick={handleManageSubscription}
                      disabled={loading}
                      className="w-full mt-3 py-2 text-sm text-[#2186EB] hover:underline"
                    >
                      Manage Subscription
                    </button>
                  )}
                </div>
              ) : plan.name === 'Free' ? (
                <button
                  onClick={() => navigate('/register')}
                  className="w-full py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition flex items-center justify-center gap-2"
                >
                  Get Started <FaArrowRight />
                </button>
              ) : (
                <button
                  onClick={() => handleUpgrade(plan.planId)}
                  disabled={loading}
                  className="w-full py-3 bg-[#2186EB] text-white rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? 'Processing...' : (
                    <>
                      {plan.cta} <FaArrowRight />
                    </>
                  )}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto mt-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">
                Can I change plans later?
              </h3>
              <p className="text-gray-600">
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">
                What payment methods do you accept?
              </h3>
              <p className="text-gray-600">
                We accept all major credit cards through Stripe. Your payment information is secure and never stored on our servers.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">
                Is my data encrypted?
              </h3>
              <p className="text-gray-600">
                Yes! All files are encrypted client-side using AES-256 encryption before upload. We never see your files in plaintext.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">
                Can I cancel anytime?
              </h3>
              <p className="text-gray-600">
                Absolutely. Cancel your subscription anytime from your dashboard. You'll continue to have access until the end of your billing period.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        {!isAuthenticated && (
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              Ready to get started? Create a free account in seconds.
            </p>
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-3 bg-[#2186EB] text-white rounded-lg font-semibold hover:bg-blue-700 transition inline-flex items-center gap-2"
            >
              Sign Up Free <FaArrowRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Pricing;

